import history from 'browserHistory'

import RoutesHelper from 'helpers/RoutesHelper'

import { fetchOrganization } from 'data/organizations/actions'
import { fetchViewer } from 'data/users/actions'
import { getCookieDomain, getApiEndpoint } from 'data/config/selectors'

import { getOrganizationDomain } from 'helpers/URLHelper'

import {
  createSuccessToasterMessage,
  createToasterMessage,
  setActiveOrganizationId,
  setActiveModal,
} from 'data/ui/actions'
import { TOASTER_TYPES } from 'data/ui/selectors'
import * as modalIds from 'components/Utils/Modal/constants'

import { recoverTokens, clearTokens } from 'data/tokenStore'
import { renewTokens } from 'data/auth/actions'
import { measureTimingEnd } from 'data/metrics'

export const APP_OFFLINE = 'APP_OFFLINE'
export const APP_ONLINE = 'APP_ONLINE'
export const BOOTSTRAP = 'BOOTSTRAP'
export const BOOTSTRAP_SUCCESS = 'BOOTSTRAP_SUCCESS'
export const BOOTSTRAP_ERROR = 'BOOTSTRAP_ERROR'

const detectQueryParamsAtBootstrap = regexp => () => {
  const query = window.document.location.search
  const result = regexp.exec(query)
  return result && result[1]
}

const detectAppOAuthSuccess = detectQueryParamsAtBootstrap(
  /app_oauth_success=(\w+)/
)
const detectSSOSetupSuccess = detectQueryParamsAtBootstrap(
  /sso_setup_success=(\w+)/
)
const detectSSOSetupFailure = detectQueryParamsAtBootstrap(
  /sso_setup_failure=(\w+)/
)
const detectChannelCloning = detectQueryParamsAtBootstrap(
  /modal_duplicate_channel=(\w+)/
)

export const start = ({ skipAuth = false } = {}) => {
  return ({ getState, dispatch }) => {
    return {
      type: BOOTSTRAP,
      payload: {
        promise: bootstrap(
          {
            getState,
            dispatch,
          },
          { skipAuth }
        ),
      },
    }
  }
}

async function bootstrap({ getState, dispatch }, { skipAuth } = {}) {
  // retrieve public organization
  let organizationId
  let tokens
  try {
    const publicOrganization = await fetchOrganization(getOrganizationDomain())(
      {
        getState,
        dispatch,
      }
    )
    organizationId = publicOrganization.id
    dispatch(setActiveOrganizationId(publicOrganization.id))
  } catch (e) {
    throw new Error('Team not found')
  }

  if (skipAuth) {
    return { authentified: false }
  }

  const recoveredTokens = recoverTokens()
  if (!recoveredTokens) {
    return { authentified: false }
  }

  const state = getState()
  const cookieDomain = getCookieDomain(state)
  const apiEndpoint = getApiEndpoint(state)

  // renew tokens
  try {
    tokens = await renewTokens({
      tokens: recoveredTokens,
      organizationId,
      cookieDomain,
      apiEndpoint,
    })
  } catch (error) {
    clearTokens()
    history.replace(RoutesHelper.login())
    return { authentified: false }
  }

  try {
    await fetchViewer({ getState, dispatch }, tokens.apiToken)
  } catch (e) {
    throw new Error('Unable to fetch user information')
  }

  // eventually display a successful app installation toaster
  const installedAppId = detectAppOAuthSuccess()
  if (installedAppId) {
    dispatch(
      createSuccessToasterMessage({
        title: `${installedAppId} integration has been successfully added to your team`,
      })
    )
  }

  // eventually display a SSO status setup toaster
  const ssoSetupSuccess = detectSSOSetupSuccess()
  const ssoSetupFailure = detectSSOSetupFailure()
  const cloneChannel = detectChannelCloning()
  if (cloneChannel) {
    dispatch(
      setActiveModal({
        id: modalIds.CHANNEL_CLONE,
        meta: { publicShareToken: cloneChannel },
      })
    )
  }
  if (ssoSetupSuccess || ssoSetupFailure) {
    dispatch(
      createToasterMessage({
        type: ssoSetupSuccess ? TOASTER_TYPES.SUCCESS : TOASTER_TYPES.ERROR,
        title: ssoSetupSuccess
          ? `Single sign-on has successfully been setup.`
          : `Single sign-on setup has failed.`,
      })
    )
    history.push(RoutesHelper.settingsAuthentication())
  }

  measureTimingEnd('TIME_TO_AUTH')

  return { authentified: true, tokens }
}
